// FILE: src/app/_components/hero-section.tsx
// START_MODULE_CONTRACT:
// PURPOSE: Hero-секция лендинга OXPA.PRO — SaaS-позиционирование платформы для ЧОП.
// SCOPE: Рендер hero с живой статистикой рынка, CTA на регистрацию.
// INPUT: totalAll, totalActive, regionsCount, lastUpdateDate — из Server Component
// OUTPUT: React-фрагмент hero-секции
// KEYWORDS: CONCEPT(SaaS): platform-positioning; TECH(YandexMetrika): reachGoal
// END_MODULE_CONTRACT

'use client';

import Link from 'next/link';
import { Button } from '@oxpa/shared/ui/button';
import { ArrowRight } from 'lucide-react';

interface HeroSectionProps {
  totalAll: number;
  totalActive: number;
  regionsCount: number;
  lastUpdateDate: string;
}

export function HeroSection({ totalAll, totalActive, regionsCount, lastUpdateDate }: HeroSectionProps) {
  const stats = [
    { value: (totalAll || 0).toLocaleString("ru-RU"), label: "ЧОПов в базе", color: "text-white" },
    { value: (totalActive || 0).toLocaleString("ru-RU"), label: "Действующих", color: "text-green-400" },
    { value: regionsCount, label: "Регионов РФ", color: "text-white" },
    { value: lastUpdateDate || "—", label: "По данным реестра Росгвардии на", color: "text-white" },
  ];

  return (
    <section className="container mx-auto px-6 py-20 border-b border-white/20 relative overflow-hidden">
      <div className="absolute inset-0 bg-oxpa-grid-lg pointer-events-none"></div>
      <div className="max-w-4xl relative z-10">
        <div className="inline-flex items-center gap-3 mb-8 px-3 py-1.5 border border-green-500/30 bg-green-500/5 text-[10px] uppercase tracking-widest text-green-400/90 shadow-[4px_4px_0px_0px_rgba(34,197,94,0.1)]">
          <span className="w-1.5 h-1.5 bg-green-400 animate-pulse" />
          ОБЛАЧНАЯ ПЛАТФОРМА
        </div>
        <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight leading-[1.1] mb-8">
          Ваш ЧОП.<br />
          <span className="text-white/40">Единый Стандарт.</span>
        </h1>
        <p className="max-w-2xl text-lg text-white/60 mb-12 font-sans leading-relaxed border-l-2 border-green-500/50 pl-6">
          Мощная облачная экосистема для охранных предприятий. CRM, оперативное управление сменами, ИИ-бот, мобильные инспекции, кадры и даже самая сложная бухгалтерия.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 mb-20">
          <Link href="/register">
            <Button
              data-ym-goal="landing_hero_cta"
              className="rounded-none h-14 px-8 text-sm bg-green-500 text-black hover:bg-green-400 uppercase tracking-[0.15em] font-bold w-full sm:w-auto shadow-[4px_4px_0px_0px_rgba(255,255,255,0.1)] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.1)] transition-all"
            >
              Попробовать бесплатно <ArrowRight className="ml-3 w-4 h-4" />
            </Button>
          </Link>
          <a href="#pricing" className="w-full sm:w-auto">
            <Button
              variant="outline"
              className="rounded-none h-14 px-8 text-sm border-white/20 bg-black text-white hover:bg-white/10 hover:text-white uppercase tracking-[0.15em] font-bold w-full shadow-[4px_4px_0px_0px_rgba(255,255,255,0.05)] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.05)] transition-all"
            >
              Тарифная сетка
            </Button>
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-white/20 border border-white/20 bg-black/80 backdrop-blur-md shadow-[8px_8px_0px_0px_rgba(255,255,255,0.05)]">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`p-6 hover:bg-white/5 transition-colors ${
                i >= 2 ? 'border-t md:border-t-0 border-white/20' : ''
              } ${i === 3 ? 'hidden sm:block' : ''}`}
            >
              <div className={`text-3xl lg:text-4xl font-black ${s.color} mb-2`}>
                {s.value}
              </div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
